import { useCallback, useEffect, useState } from 'react';

import axios from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { createContainer, useContainer } from 'unstated-next';

import AdminAuthContainer from './AdminAuthContainer';
import { AdminTabs } from './config/adminSettingsConfig';
import { flattenData, transformSettingsData } from './utils/transformSettingsData';
import { RECORDING_USAGE } from '../../config/apiPaths';
import { DEFAULT_ADMIN_SETTINGS, SETTINGS_API } from '../../config/settingsConfig';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { useSchoolInfo } from '../../hooks/useSchoolInfo';
import { type OrgSettings } from '../../types/adminConfig';
import { type GetAdminSettings } from '../../types/apiTypes';
import { type ResponseRecordingUsage } from '../../types/requests/recordingUsage';
import { getUserUuidHeaders } from '../../utils/httpUtils';
import { parseOauthState } from '../../utils/oauthState';
import { getUrls, processApiPath } from '../../utils/url';

const useInitialAdminSettingsTab = () => {
    const location = useLocation();
    const defaultTab = Object.values(AdminTabs).find((tab) => location.pathname.includes(tab));
    return defaultTab;
};

const useAdminSettings = () => {
    const { updateSettingsState } = useOrgSettings(true);
    const DEFAULT_TAB = useInitialAdminSettingsTab();
    const [activeTab, setActiveTab] = useState<AdminTabs | undefined>(DEFAULT_TAB);
    const history = useHistory();
    const { path } = useRouteMatch();
    const [adminSettings, setAdminSettings] = useState<OrgSettings>(cloneDeep(DEFAULT_ADMIN_SETTINGS.academic));
    const [importRecordings, setImportRecordings] = useState<boolean>(false); // Not in adminSettings, as it is a request rather than a state
    const [organizationName, setOrganizationName] = useState<string>('');
    const [initialLoad, setInitialLoad] = useState<boolean>(true);
    const [showOrgType, setShowOrgType] = useState<boolean>(false);
    const [showExperimentalSettings, setShowExperimentalSettings] = useState<boolean>(false);

    const { enqueueSnackbar } = useSnackbar();
    const [isSavingSettingsData, setSavingData] = useState(false);
    const [isSavingDisabled, setSavingDisabled] = useState(false);
    const location = useLocation();

    const { loading: loadingSchool, data: school } = useSchoolInfo();

    const { t } = useTranslation('admin_settings', { useSuspense: false });
    const { api } = getUrls();

    const { data: schoolInfo } = useSchoolInfo();
    const schoolUuid = schoolInfo?.uuid as string;

    const [isLoadingRecordingUsage, setIsLoadingRecordingUsage] = useState(false);
    const [usageStats, setUsageStats] = useState<ResponseRecordingUsage | undefined>();

    const {
        uuid: userUuid,
        isAdmin,
        canManageClasses,
        isLoading: isAdminAuthLoading
    } = useContainer(AdminAuthContainer);

    const handleSaveSettings = async () => {
        setSavingData(true);

        const userUuid = window.localStorage?.user_uuid;
        try {
            await axios.post(`${api}${SETTINGS_API}`, {
                userUuid,
                importRecordings,
                data: flattenData(adminSettings)
            });
            updateSettingsState(adminSettings);
            enqueueSnackbar(t('components.saveButton.saveSuccess'), { variant: 'success' });
            setSavingData(false);
        } catch (error) {
            enqueueSnackbar(t('components.saveButton.saveError'), { variant: 'error' });
            setSavingData(false);
        }
    };

    useEffect(() => {
        const oauthState = parseOauthState();
        const search = new URLSearchParams(location.search);

        if (oauthState) {
            setShowOrgType(oauthState.showOrgType || false);
            setShowExperimentalSettings(oauthState.showExperimentalSettings || false);
        } else {
            setShowOrgType(search.get('showOrgType') === 'true');
            setShowExperimentalSettings(search.get('showExperimentalSettings') === 'true');
        }
    }, [location.search]);

    useEffect(() => {
        const getSettings = async () => {
            try {
                const { data: body } = await axios.get<GetAdminSettings>(`${api}${SETTINGS_API}?dumpCache=1`);
                const { data, prefix } = body;

                setAdminSettings(transformSettingsData(data));

                if (prefix) {
                    setOrganizationName(prefix);
                }

                setInitialLoad(false);
            } catch (error) {
                console.error('Failed to fetch admin settings', error);
                // TODO: set error state and render error component?
            }
        };

        getSettings();
    }, [api]);

    useEffect(() => {
        if (isAdminAuthLoading || activeTab) return;

        let defaultTab = !isAdmin && canManageClasses ? AdminTabs.Classes : AdminTabs.Features;

        setActiveTab(defaultTab);
        history.push(`${path}${defaultTab}`);
    }, [isAdminAuthLoading, isAdmin, canManageClasses, activeTab, history, path]);

    const fetchRecordingUsage = useCallback(async (): Promise<ResponseRecordingUsage | undefined> => {
        if (!schoolUuid || !userUuid) {
            return Promise.resolve(undefined);
        }
        const url = processApiPath(RECORDING_USAGE, { schoolUuid });
        try {
            const { data, status } = await axios.get<Promise<ResponseRecordingUsage>>(url, {
                headers: getUserUuidHeaders(userUuid)
            });
            if (status !== 200) {
                throw new Error('Request failed by returning unexpected status');
            }
            return data;
        } catch (e) {
            console.error('Failed to recover recording usage data', e);
            return undefined;
        }
    }, [userUuid, schoolUuid]);

    useEffect(() => {
        if (!isAdmin) return;
        const loadRecordingUsage = async () => {
            setIsLoadingRecordingUsage(true);
            const data = await fetchRecordingUsage();
            setIsLoadingRecordingUsage(false);
            setUsageStats(data);
        };
        loadRecordingUsage();
    }, [fetchRecordingUsage, isAdmin]);

    return {
        activeTab,
        setActiveTab,
        school,
        loadingSchool,
        adminSettings,
        setAdminSettings,
        setImportRecordings,
        organizationName,
        initialLoad,
        showOrgType,
        showExperimentalSettings,
        handleSaveSettings,
        isSavingSettingsData,
        isSavingDisabled,
        setSavingDisabled,
        usageStats,
        isLoadingRecordingUsage
    };
};

const AdminSettingsContainer = createContainer(useAdminSettings);

export default AdminSettingsContainer;
